import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ConfigService } from './config.service';
import {
  QuickApprovalRequest,
  QuickApprovalResponse,
  ReguaSegment,
  PreOferta,
  PreOfertaStatus
} from '../interfaces/quick-approval.interface';
import { LocalStorageUtil } from '../utils/local-storage.util';

type ValidPreOferta = Pick<ReguaSegment, 'startPercent' | 'endPercent' | 'status'> & {
  position: number;
  originalIndex: number;
};

/**
 * Serviço responsável por consultar a API Quick Approval
 * e processar os dados da régua de aprovação
 */
@Injectable({
  providedIn: 'root'
})
export class QuickApprovalService {
  private readonly API_URL: string;

  constructor(private http: HttpClient, protected configService: ConfigService) {
    this.API_URL = this.configService.getRequiredByPath<string>('apiQuickApprovalUrl');
  }

  /**
   * Mapeia status da pré-oferta para cor da régua
   */
  private getColorByStatus(status: PreOfertaStatus): string {
    const colorMap: Record<PreOfertaStatus, string> = {
      'Rejected': '#8C8C8C', // Cinza (rejeitado)
      'Credit_Officer_Decision': '#FFB81C', // Laranja (mesa de negociação)
      'Approved': '#00B0F0' // Azul (aprovado)
    };
    return colorMap[status];
  }

  /**
   * Converte uma faixa numérica da API, como "10,00-19,99", em percentuais.
   */
  private parseFaixaEntrada(
    faixaEntrada: string | null | undefined
  ): { startPercent: number; endPercent: number } | null {
    const match = /^\s*(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)\s*$/.exec(faixaEntrada ?? '');
    if (!match) {
      return null;
    }

    const startValue = Number(match[1].replace(',', '.'));
    const endValue = Number(match[2].replace(',', '.'));
    if (!Number.isFinite(startValue) || !Number.isFinite(endValue) || endValue < startValue) {
      return null;
    }

    return {
      startPercent: Math.min(Math.round(startValue), 90),
      endPercent: Math.min(Math.round(endValue), 90),
    };
  }

  private isPreOfertaStatus(status: unknown): status is PreOfertaStatus {
    return status === 'Rejected' || status === 'Credit_Officer_Decision' || status === 'Approved';
  }

  /**
   * Processa as pré-ofertas e gera os segmentos da régua
   * Ignora ofertas sem status/faixa válidos e agrupa as faixas ordenadas por status.
   */
  processarPreOfertas(preOfertas: readonly PreOferta[] | null | undefined): ReguaSegment[] {
    if (!preOfertas || preOfertas.length === 0) {
      return [];
    }

    const validOffers: ValidPreOferta[] = [];

    preOfertas.forEach((offer, originalIndex) => {
      if (!offer || !this.isPreOfertaStatus(offer.status)) {
        return;
      }

      const range = this.parseFaixaEntrada(offer.faixaEntrada);
      if (!range) {
        return;
      }

      validOffers.push({
        ...range,
        status: offer.status,
        position: offer.posicao,
        originalIndex,
      });
    });

    validOffers.sort(
      (first, second) =>
        first.position - second.position || first.originalIndex - second.originalIndex,
    );

    const segments: ReguaSegment[] = [];

    for (const offer of validOffers) {
      const previousSegment = segments[segments.length - 1];
      if (!previousSegment || previousSegment.status !== offer.status) {
        segments.push({
          startPercent: offer.startPercent,
          endPercent: offer.endPercent,
          status: offer.status,
          color: this.getColorByStatus(offer.status),
        });
        continue;
      }

      previousSegment.endPercent = offer.endPercent;
    }

    return segments;
  }

  /**
   * Chama a API Quick Approval e retorna os segmentos da régua
   */
  getQuickApproval(request: QuickApprovalRequest): Observable<ReguaSegment[]> {
    if (this.configService.isDebugEnabled()) {
      console.log('📤 Enviando request para Quick Approval:', request);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<QuickApprovalResponse>(this.API_URL, request, { headers }).pipe(
      map(response => {
        if (this.configService.isDebugEnabled()) {
          console.log('✅ Resposta da API Quick Approval:', response);
        }
        return this.processarPreOfertas(response.preOfertas);
      }),
      catchError(error => {
        console.error('Erro ao buscar Quick Approval:', error);
        if (error.error) {
          console.error('Detalhes do erro API:', error.error);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Monta o request com base nos dados do lead
   */
  buildQuickApprovalRequest(leadDetails: any): QuickApprovalRequest {
    // Validações básicas para evitar erro 500
    const idSimulacao = this.toNumber(leadDetails.idSimulacao || leadDetails.codigoSimulacao);
    const idMontadora = this.toNumber(leadDetails.idMontadora);

    // Se IdSimulacaoFrontend for 0, usar um valor padrão para evitar erro
    const idSimulacaoFrontend = idSimulacao > 0 ? idSimulacao : 1; // Valor padrão para testes

    // Se IdMontadora for 0, usar um valor padrão
    const idMontadoraFinal = idMontadora > 0 ? idMontadora : 1; // Valor padrão

    return {
      IdSimulacaoFrontend: idSimulacaoFrontend,
      NumeroDN: this.toNumber(leadDetails.dn),
      IdMontadora: idMontadoraFinal,
      Documento: this.limparDocumento(leadDetails.cpf || leadDetails.cpfCnpj || ''),
      Status: 'R', // Padrão
      Nome: leadDetails.nome || '',
      Telefone: this.limparTelefone(leadDetails.celular || leadDetails.telefone || ''),
      Email: leadDetails.email || '',
      DataNascimento: this.formatarDataISO(leadDetails.dataNascimento),
      NumeroAnoModelo: this.toNumber(leadDetails.anoModelo) || new Date().getFullYear(),
      NumeroAnoFabricacao: this.toNumber(leadDetails.anoFabricacao) || new Date().getFullYear(),
      DescricaoModelo: `${leadDetails.modeloGrupo || ''} ${leadDetails.versao || ''}`.trim() || 'Modelo não informado',
      TipoCombustivel: this.toNumber(leadDetails.tipoCombustivel),
      ValorFinanciado: this.toNumber(leadDetails.valorFinanciado),
      ValorEntrada: this.toNumber(leadDetails.valorEntrada),
      QuantidadeParcelas: this.toNumber(leadDetails.quantidadeParcelas),
      ValorParcela: this.toNumber(leadDetails.valorParcela),
      ValorNotaFiscal: this.toNumber(leadDetails.valorSimulacao),
      Uf: leadDetails.estado || 'SP',
      NumeroMolicar: leadDetails.cdMolicar || '',
      FlagEmail: 'N',
      FlagConsentimento: 'S',
      TipoUso: this.formatarTipoUso(leadDetails.tipoUso),
      Usuario: LocalStorageUtil.getVWUser().name,
      FlagPreAprovado: leadDetails.preAprovado || null
    };
  }

  /**
   * Remove caracteres especiais do CPF/CNPJ
   */
  private limparDocumento(documento: string): string {
    return documento.replace(/\D/g, '');
  }

  /**
   * Remove caracteres especiais do telefone
   */
  private limparTelefone(telefone: string): string {
    return telefone.replace(/\D/g, '');
  }

  /**
   * Converte valor para número de forma segura
   */
  private toNumber(value: any): number {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }

  /**
   * Formata e valida o Tipo de Uso (N ou U)
   * A API exige max length 2.
   */
  private formatarTipoUso(tipo: string): string {
    if (!tipo) return 'N';

    // Normalizar para maiúsculo
    const upper = typeof tipo === 'string' ? tipo.toUpperCase() : String(tipo);

    // Se começar com N ou 0, assume Novo (N)
    if (upper.startsWith('N') || upper === '0') return 'N';

    // Se começar com U, S (Semi-novo) ou 1, assume Usado (U)
    if (upper.startsWith('U') || upper.startsWith('S') || upper === '1') return 'U';

    // Proteção: Retornar apenas a primeira letra se não reconhecido
    return upper.substring(0, 1) || 'N';
  }

  /**
   * Formata data para AAAA-MM-DD (formato esperado por APIs legadas/financeiras)
   * Se a data for inválida ou resultar em menor de idade (proteção para dados de teste), retorna 1980-01-01
   */
  private formatarDataISO(data: string | Date | null): string {
    const defaultDate = '1980-01-01';

    if (!data) return defaultDate;

    const dateObj = typeof data === 'string' ? new Date(data) : data;

    // Verificar se data é válida
    if (isNaN(dateObj.getTime())) {
      if (this.configService.isDebugEnabled()) {
        console.warn('Data de nascimento inválida:', data);
      }
      return defaultDate;
    }

    // Proteção contra datas futuras ou menores de idade (causa comum de erro 500 em APIs de crédito)
    const currentYear = new Date().getFullYear();
    const birthYear = dateObj.getFullYear();
    if (birthYear > currentYear - 18) {
      if (this.configService.isDebugEnabled()) {
        console.warn(`Data de nascimento indica menor de idade (${birthYear}). Usando data padrão para evitar erro 500.`);
      }
      return defaultDate;
    }

    // Retorna apenas a parte da data YYYY-MM-DD
    return dateObj.toISOString().split('T')[0];
  }
}
